import { router } from "@inertiajs/react";
import { Autocomplete, type BoxProps, Button, Text } from "@mantine/core";
import { type UseFormReturnType, useForm } from "@mantine/form";
import { modals } from "@mantine/modals";
import { IconPlus, IconUserBolt, IconUserPause } from "@tabler/icons-react";
import { type ReactElement, type ReactNode, useState } from "react";
import { UserDataTable, type UserDataTableProps } from "../../components/datatable/UserDataTable";
import { FilterSection } from "../../components/filter/FilterSection";
import { showDeleteModal } from "../../helpers/modal";
import { getFullName } from "../../helpers/stringHelper";
import { ContentShell } from "../../layout/ContentShell";
import type { UserType } from "../../models/user";
import { users_activate_path, users_deactivate_path } from "../../routes";

type UsersProps<T extends UserType, E extends UserFilterFormType> = UserDataTableProps<T> & {
  title: string;
  actionButtons?: ReactNode[];
  userTypeStringPlural: string;
  onNew: () => void;
  filterFormData: E;
  getAdditionalFilters?: (form: UseFormReturnType<E>) => ReactElement<BoxProps>[];
  onFilterSubmit: (values: E) => void;
  userNames: string[];
  onReset: () => void;
};

export type UserFilterFormType = {
  name?: string;
};

export const resetUserFilter = {
  name: undefined,
};

export default function Users<T extends UserType, E extends UserFilterFormType>({
  title,
  onDelete,
  onNew,
  actionButtons,
  userTypeString,
  userTypeStringPlural,
  viewOnly,
  filterFormData,
  getAdditionalFilters,
  onFilterSubmit,
  userNames,
  onReset,
  ...props
}: UsersProps<T, E>) {
  const [selectedRecords, setSelectedRecord] = useState<T[]>([]);
  const filterForm = useForm<E>({
    mode: "uncontrolled",
    initialValues: {
      name: "",
      ...filterFormData,
    },
  });

  const deactivateUsers = () => {
    const users = selectedRecords.map((userType) => userType.user.email);

    modals.openConfirmModal({
      title: `${userTypeStringPlural} deaktivieren`,
      children: (
        <Text>{`Möchten Sie wirklich ${selectedRecords.length} ${selectedRecords.length > 1 ? userTypeStringPlural : userTypeString} deaktivieren`}</Text>
      ),
      labels: { confirm: "Deaktivieren", cancel: "Abbrechen" },
      confirmProps: { color: "red" },
      onConfirm: () => {
        router.patch(users_deactivate_path(), { users });
        setSelectedRecord([]);
      },
    });
  };

  const activateUsers = () => {
    const users = selectedRecords.map((userType) => userType.user.email);

    modals.openConfirmModal({
      title: `${userTypeStringPlural} aktivieren`,
      children: (
        <Text>{`Möchten Sie wirklich ${selectedRecords.length} ${selectedRecords.length > 1 ? userTypeStringPlural : userTypeString} aktivieren`}</Text>
      ),
      labels: { confirm: "Aktivieren", cancel: "Abbrechen" },
      confirmProps: { color: "zli-green" },
      onConfirm: () => {
        router.patch(users_activate_path(), { users });
        setSelectedRecord([]);
      },
    });
  };

  const openDeactivateModal = (user: T) =>
    showDeleteModal({
      title: `${userTypeString} ${getFullName(user)}`,
      article: "den",
      onConfirm: () => onDelete(user),
    });

  return (
    <ContentShell
      title={title}
      actions={
        !viewOnly && (
          <>
            <Button leftSection={<IconPlus />} variant="light" onClick={onNew}>
              Neu
            </Button>
            {actionButtons}
            <Button
              disabled={selectedRecords.length === 0}
              leftSection={<IconUserBolt />}
              color="zli-green"
              variant="light"
              onClick={activateUsers}
            >
              Aktivieren
            </Button>
            <Button
              disabled={selectedRecords.length === 0}
              leftSection={<IconUserPause />}
              color="red"
              variant="light"
              onClick={deactivateUsers}
            >
              Deaktivieren
            </Button>
          </>
        )
      }
    >
      <FilterSection
        filters={[
          <Autocomplete data={userNames} label="Name" key={filterForm.key("name")} {...filterForm.getInputProps("name")} />,
          ...(getAdditionalFilters?.(filterForm) ?? []),
        ]}
        form={filterForm}
        onSubmit={onFilterSubmit}
        onReset={onReset}
      />
      <UserDataTable
        viewOnly={viewOnly}
        userTypeString={userTypeString}
        onDelete={openDeactivateModal}
        onSelectedRecordsChange={setSelectedRecord}
        selectedRecords={selectedRecords}
        {...props}
      />
    </ContentShell>
  );
}
